import { useState, useMemo, useRef, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { getChatAppAppearanceModel } from '../lib/chatAppCharacters';

function CharacterModel({ modelGlb, isHovered }) {
  const gltf = useGLTF(modelGlb);
  const mixerRef = useRef();
  const actionsRef = useRef({});
  const currentKeyRef = useRef(null);
  const hoverPhaseRef = useRef(null);

  const clonedScene = useMemo(() => skeletonClone(gltf.scene), [gltf.scene]);

  useEffect(() => {
    const mixer = new THREE.AnimationMixer(clonedScene);
    mixerRef.current = mixer;

    const idleClip = gltf.animations.find((c) => c.name === 'idle');
    const waveClip = gltf.animations.find((c) => c.name === 'greeting');

    if (idleClip) {
      actionsRef.current.idle = mixer.clipAction(idleClip);
      actionsRef.current.idle.loop = THREE.LoopRepeat;
      actionsRef.current.idle.play();
      currentKeyRef.current = 'idle';
    }
    if (waveClip) {
      actionsRef.current.wave = mixer.clipAction(waveClip);
      actionsRef.current.wave.loop = THREE.LoopOnce;
      actionsRef.current.wave.clampWhenFinished = true;
    }

    // When wave finishes, go to idle
    mixer.addEventListener('finished', (e) => {
      if (e.action === actionsRef.current.wave) {
        hoverPhaseRef.current = 'done';
        if (actionsRef.current.idle) {
          e.action.crossFadeTo(actionsRef.current.idle.reset().play(), 0.3, true);
          currentKeyRef.current = 'idle';
        }
      }
    });

    return () => mixer.stopAllAction();
  }, [clonedScene, gltf.animations]);

  useFrame((_, delta) => {
    mixerRef.current?.update(delta);

    let targetKey;
    if (isHovered) {
      targetKey = hoverPhaseRef.current === 'done' ? 'idle' : 'wave';
    } else {
      targetKey = 'idle';
      hoverPhaseRef.current = null;
    }

    if (targetKey !== currentKeyRef.current && actionsRef.current[targetKey]) {
      const current = actionsRef.current[currentKeyRef.current];
      const next = actionsRef.current[targetKey];
      if (current) {
        current.crossFadeTo(next.reset().play(), 0.25, true);
      } else {
        next.reset().play();
      }
      currentKeyRef.current = targetKey;
      if (targetKey === 'wave') hoverPhaseRef.current = 'playing';
    }
  });

  return (
    <group position={[0, -0.35, 0]}>
      <primitive object={clonedScene} scale={0.14} />
    </group>
  );
}

export default function AgentPreview({ modelKey, onPrevModel, onNextModel }) {
  const [hovered, setHovered] = useState(false);
  const model = getChatAppAppearanceModel(modelKey);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        borderRadius: '12px 0 0 12px',
        overflow: 'hidden',
        background: 'rgba(255,255,255,0.02)',
        position: 'relative',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Canvas
        camera={{ position: [0, 0.05, 3.2], fov: 28 }}
        style={{ background: 'transparent' }}
      >
        <ambientLight intensity={0.5} />
        <directionalLight position={[2, 3, 2]} intensity={1} />
        <CharacterModel modelGlb={model.glb} isHovered={hovered} />
      </Canvas>

      {/* Character selector arrows */}
      {onPrevModel && onNextModel && (
        <div style={{
          position: 'absolute',
          bottom: 8, left: 0, right: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 8, zIndex: 2,
        }}>
          <button onClick={onPrevModel} style={arrowBtn}>◀</button>
          <span style={{
            fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
            fontSize: 9,
            color: 'rgba(255,255,255,0.5)',
            minWidth: 60,
            textAlign: 'center',
          }}>
            {model.label}
          </span>
          <button onClick={onNextModel} style={arrowBtn}>▶</button>
        </div>
      )}
    </div>
  );
}

const arrowBtn = {
  width: 24, height: 24,
  background: 'rgba(8,10,16,0.7)',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: 6,
  color: 'rgba(255,255,255,0.6)',
  fontSize: 10,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
